/* eslint-disable no-unused-expressions */
/* eslint-disable class-methods-use-this */
import { makeAutoObservable } from 'mobx';
import { MapStore } from '@adactiveasia/adsign-map';
import { fetchCMSData, PAGES } from '../utils';
import moment from 'moment';
import { config } from '../utils/constants';

class MainStore {
    mapStore: MapStore;

    // Data
    pois = [];
    amenities = [];
    screensavers = [];
    events = [];
    promotions = [];
    artMurals = [];
    listMenu = [];
    listAmenities = [];
    config = {};

    // Data - Information
    information = [];
    informationParkingRates = null;

    // Data - Visual
    logo = null;
    backgroundApp = null;
    placeholderImage = null;

    // -----------------------------
    // App & Page
    progressLoadApp = 0;
    isAppLoaded = false;
    currentPage = PAGES.HOMEPAGE;
    currentHeader = null;

    // Search History
    typeSearchHistory = null;
    searchKeyword = null;

    // Keyboard
    isKeyboardVisible = false;
    keyboardKeyword = '';
    keyboardType = 'alpha';

    // Poi or Media Overlay
    isPoiOverlay = false;
    selectedPoiDetail = null;
    selectedMediaDetail = null;

    // Tooltip
    isShowTooltip = false;

    // Floorguide
    startPoi = null;
    destinationPoi = null;

    // Amenities
    isAmenitiesOverlay = false;
    selectedAmenity = null;

    // Wayfinding
    wayfindingShapeFrom = null;
    wayfindingShapeTo = null;

    // Media Page
    mediaPagination = { current: 1, perPage: 6 };

    // Screensaver
    isScreensaverVisible = false;
    screensaverIndex = 0;
    isAccessibleMode = false;

    // Floorguide Page
    floorguideIsDirectionCardVisible = false;
    floorguideDirectionIndex = 0;
    floorguideIsMinimizedDirectionCard = false;
    floorguideSelectedAmenity = null;

    // Directory Page
    directorySelectorType = null;
    directorySelectedCategories = [];
    directorySelectedLevels = [];
    directoryIsCategoryShow = false;
    directoryIsFloorShow = false;
    directoryPagination = { current: 1, perPage: 12 };
    filteredPois = this.pois;

    constructor() {
        this.mapStore = new MapStore();
        makeAutoObservable(this, {
            mapStore: false, // Exclude mapStore from being observable
        });
    }

    static createMapStore() {
        return new MapStore();
    }

    async loadApp() {
        try {
            const totalSteps = 8;
            let currentStep = 0;

            const updateProgress = () => {
                const percent = Math.round((currentStep / totalSteps) * 100);
                this.setProgressLoadApp(percent);
            };

            this.config = config;
            currentStep++;
            updateProgress();

            const cmsData = await fetchCMSData();

            this.pois = cmsData.pois?.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            currentStep++;
            updateProgress();

            this.setAmenities(cmsData.pois || [], config.tenantIds.amenitiesId);
            currentStep++;
            updateProgress();

            this.setScreensavers(
                cmsData.media || [],
                config.mediaIds.screensaverId
            );
            this.setEvents(cmsData.information || [], config.mediaIds.eventsId);
            this.setPromotions(
                cmsData.information || [],
                config.mediaIds.promotionsId
            );
            this.setArtMurals(cmsData.media || [], config.mediaIds.artMuralsId);
            currentStep++;
            updateProgress();

            this.setListMenu(cmsData.information || [], config.listMenuId);
            this.setListAmenities(
                cmsData.information || [],
                config.listAmenitiesId
            );
            this.setVisualData(cmsData.information || [], config);
            this.setInformationData(cmsData.information || [], config);
            currentStep++;
            updateProgress();

            if (this.screensavers.length > 0) {
                this.setIsScreensaverVisible(true);
            }

            this.updateFilteredPois();
            currentStep++;
            updateProgress();
            // this.setProgressLoadApp(100);
        } catch (error) {
            // console.error('Failed to load app:', error);
            this.setAppLoaded(false);
        }
    }

    setAmenities(data, amenitiesId) {
        this.amenities = data.filter((e) =>
            e.categoryIds.includes(amenitiesId)
        );
    }
    setScreensavers(data, screensaverId) {
        this.screensavers = data.filter((e) => e.categoryId === screensaverId);
    }
    setEvents(data, eventsId) {
        this.events = data.filter((e) => e.categoryId === eventsId);
    }
    setPromotions(data, promotionsId) {
        this.promotions = data.filter((e) => e.categoryId === promotionsId);
    }
    setArtMurals(data, artMuralsId) {
        this.artMurals = data.filter((e) => e.categoryId === artMuralsId);
    }
    setListMenu(data, listMenuId) {
        this.listMenu = data
            .filter((e) => e.categoryId === listMenuId)
            .filter((media) => {
                if (media.start && media.end) {
                    return moment(new Date()).isBetween(
                        moment(media.start).startOf('minute').toISOString(),
                        moment(media.end).startOf('minute').toISOString()
                    );
                }
                return true;
            });
    }
    setListAmenities(data, listAmenitiesId) {
        this.listAmenities = data.filter(
            (e) => e.categoryId === listAmenitiesId
        );
    }
    setInformationData(informations, config) {
        const { buildingInfoId } = config;

        this.information = informations;

        this.informationParkingRates = informations.find(
            (info) => info._id === buildingInfoId
        );
    }
    setVisualData(data, config) {
        const { logoId, backgroundAppId, placeholderId } = config.visualIds;

        const getImage = (id) =>
            data.find((item) => item._id === id)?.files?.[0]?.logo || null;

        const getFile = (id) =>
            data.find((item) => item._id === id)?.files?.[0] || null;

        this.logo = getImage(logoId);
        this.backgroundApp = getFile(backgroundAppId);
        this.placeholderImage = getImage(placeholderId);
    }

    // -----------------------------
    // App & Page
    setProgressLoadApp(value) {
        this.progressLoadApp = value;
    }
    setAppLoaded(value) {
        this.isAppLoaded = value;
    }
    setCurrentPage(pageName) {
        this.currentPage = pageName;

        if (pageName === PAGES.HOMEPAGE) {
            this.currentHeader = null;
        } else {
            const menu = this.listMenu.find((m) => m.url === pageName);
            this.currentHeader = menu.description;
        }
    }

    // Search History
    setTypeSearchHistory(value) {
        this.typeSearchHistory = value;
    }
    setSearchKeyword(value) {
        this.searchKeyword = value;
    }

    // Keyboard
    setIsKeyboardVisible(value) {
        this.isKeyboardVisible = value;
    }
    setKeyboardKeyword(keyword) {
        this.keyboardKeyword = keyword;
    }
    setKeyboardType(type) {
        this.keyboardType = type;
    }

    // Poi or Media Overlay
    setIsPoiOverlay(value) {
        this.isPoiOverlay = value;
    }
    setSelectedPoiDetail(poi) {
        this.selectedPoiDetail = poi;
    }
    setSelectedMediaDetail(media) {
        this.selectedMediaDetail = media;
    }

    // Tooltip
    setIsShowTooltip(value) {
        this.isShowTooltip = value;
    }

    // Amenities
    setIsAmenitiesOverlay(value) {
        this.isAmenitiesOverlay = value;
    }
    setSelectedAmenity(value) {
        this.selectedAmenity = value;
    }

    // Wayfinding
    setWayfindingShapeFrom(shape) {
        this.wayfindingShapeFrom = shape;
    }
    setWayfindingShapeTo(shape) {
        this.wayfindingShapeTo = shape;
    }

    // Floorguide
    setStartPoi(poi) {
        this.startPoi = poi;
    }
    setDestinationPoi(poi) {
        this.destinationPoi = poi;
    }

    // Media Page
    setMediaPagination(currentPage, perPage = 6) {
        this.mediaPagination = { current: currentPage, perPage };
    }

    // Screensaver
    setIsScreensaverVisible(value) {
        this.isScreensaverVisible = value;
        if (value === true) {
            setTimeout(() => {
                this.resetApp();
                this.isAccessibleMode = false;
                this.currentPage = PAGES.HOMEPAGE;
            }, 500);
        }
    }
    setScreensaverIndex(index) {
        this.screensaverIndex = index;
    }

    // Accessibility
    setIsAccessibleMode(value) {
        this.isAccessibleMode = value;
    }

    // Floorguide Page
    setFloorguideDirectionIndex(index) {
        this.floorguideDirectionIndex = index;
    }
    setFloorguideIsDirectionCardVisible(value) {
        this.floorguideIsDirectionCardVisible = value;
    }
    setFloorguideIsMinimizedDirectionCard(value) {
        this.floorguideIsMinimizedDirectionCard = value;
    }
    setFloorguideSelectedAmenity(amenity) {
        this.floorguideSelectedAmenity = amenity;
    }

    // Directory Page
    setDirectorySelectorType(type) {
        this.directorySelectorType = type;
    }
    setDirectorySelectedCategories(categories) {
        this.directorySelectedCategories = categories;
    }
    setDirectorySelectedLevels(levels) {
        this.directorySelectedLevels = levels;
    }
    setDirectoryIsCategoryShow(value) {
        this.directoryIsCategoryShow = value;
    }
    setDirectoryIsFloorShow(value) {
        this.directoryIsFloorShow = value;
    }
    setDirectoryPagination(currentPage, perPage = 12) {
        this.directoryPagination = { current: currentPage, perPage };
    }

    updateFilteredPois() {
        const keywordUpper = this.searchKeyword?.trim().toUpperCase();

        const filteredPois = this.pois
            .filter(
                (poi) => poi.categoryId !== this.config.tenantIds.amenitiesId
            )
            .filter((poi) => {
                if (!keywordUpper) return true;

                const matchName = poi.name
                    ?.toUpperCase()
                    .split(/\s+/)
                    .some((word) => word.startsWith(keywordUpper));
                return matchName;
            });

        this.filteredPois = filteredPois;
    }

    resetSearchHistory() {
        this.setTypeSearchHistory(null);
        this.setSearchKeyword(null);
        this.setIsPoiOverlay(false);
        this.setSelectedPoiDetail(null);
    }

    // Reset State
    resetDirectory() {
        this.directorySelectorType = null;
        this.directorySelectedCategories = [];
        this.directorySelectedLevels = [];
        this.directoryPagination = { current: 1, perPage: 12 };
        this.filteredPois = this.pois;
        this.directoryIsCategoryShow = false;
        this.directoryIsFloorShow = false;

        this.isKeyboardVisible = false;
        this.keyboardKeyword = '';
        this.keyboardType = 'alpha';
    }
    resetApp(isAccessibleMode = false) {
        this.isPoiOverlay = false;
        this.selectedPoiDetail = null;
        this.selectedMediaDetail = null;

        this.wayfindingShapeTo = null;

        this.mediaPagination = { current: 1, perPage: 6 };

        this.floorguideIsDirectionCardVisible = false;
        this.floorguideDirectionIndex = 0;
        this.floorguideIsMinimizedDirectionCard = false;
        this.floorguideSelectedAmenity = null;

        this.isAccessibleMode = isAccessibleMode;

        this.resetDirectory();
    }
}

export default MainStore;
